import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { generateConfig } from "../../utils/api";
import { handleApiError } from "../../utils/apiErrorHandler";
import { toast } from "react-toastify";

// Fetch all client journeys (optionally filter by clientId or journeyTemplateId)
export const fetchClientJourneys = createAsyncThunk(
  "clientJourney/fetchAll",
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });
      const query = new URLSearchParams(params).toString();
      const url = `${baseURL}/api/client-journeys${query ? `?${query}` : ''}`;
      const response = await axios.get(url, config);
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue);
    }
  }
);

// Fetch a client's journey by client ID
export const fetchClientJourneyById = createAsyncThunk(
  "clientJourney/fetchClientJourneyById",
  async (clientId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });
      const response = await axios.get(`${baseURL}/api/client-journeys/${clientId}`, config);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null; // Return null if no journey is found
      }
      console.error("Error fetching client journey:", error);
      return handleApiError(error, rejectWithValue);
    }
  }
);

// Create a new client journey
export const createClientJourney = createAsyncThunk(
  "clientJourney/create",
  async (journeyData, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });
      const response = await axios.post(`${baseURL}/api/client-journeys`, journeyData, config);
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue);
    }
  }
);

// Update a client journey
export const updateClientJourney = createAsyncThunk(
  "clientJourney/update",
  async ({ id, journeyData }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });
      const response = await axios.patch(`${baseURL}/api/client-journeys/${id}`, journeyData, config);
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue);
    }
  }
);

// Delete a client journey
export const deleteClientJourney = createAsyncThunk(
  "clientJourney/delete",
  async (id, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });
      const response = await axios.delete(`${baseURL}/api/client-journeys/${id}`, config);
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue);
    }
  }
);

// Fetch all journey templates
export const fetchJourneyTemplates = createAsyncThunk(
  "journey/fetchJourneyTemplates",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });
      const response = await axios.get(`${baseURL}/api/journeys`, config);
      return response.data;
    } catch (error) {
      console.error("Error fetching journey templates:", error);
      return handleApiError(error, rejectWithValue);
    }
  }
);

// Assign a journey to a client
export const assignJourneyToClient = createAsyncThunk(
  "clientJourney/assignJourneyToClient",
  async ({ clientId, journeyTemplateId }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });
      const response = await axios.post(
        `${baseURL}/api/client-journeys`,
        { clientId, journeyTemplateId },
        config
      );
      toast.success("Journey assigned successfully");
      return response.data;
    } catch (error) {
      console.error("Error assigning journey:", error);
      toast.error(error.response?.data?.error || "Failed to assign journey");
      return handleApiError(error, rejectWithValue);
    }
  }
);

// Update client journey progress
export const updateClientJourneyProgress = createAsyncThunk(
  'clientJourney/updateProgress',
  async ({ clientId, progressData }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });
      const response = await axios.put(`${baseURL}/api/client-journeys/${clientId}`, progressData, config);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update progress');
    }
  }
);